import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Spin, Alert, Tag, Button, Timeline, Empty } from 'antd';
import { 
  CrownOutlined, 
  CalendarOutlined, 
  DollarOutlined, 
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import subscriptionService from '../../services/subscriptionService';
import './SubscriptionDetails.css';

const SubscriptionDetails = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const [currentResponse, historyResponse] = await Promise.all([
        subscriptionService.getMySubscription(),
        subscriptionService.getMySubscriptionHistory()
      ]);

      if (currentResponse.statusCode === 200) {
        setSubscriptions(currentResponse.data || []);
      }

      if (historyResponse.statusCode === 200) {
        setSubscriptionHistory(historyResponse.data || []);
      }
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'green';
      case 'EXPIRED':
        return 'red';
      case 'CANCELLED':
        return 'orange';
      case 'SUSPENDED':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircleOutlined />;
      case 'EXPIRED':
        return <CloseCircleOutlined />;
      case 'CANCELLED':
        return <CloseCircleOutlined />;
      case 'SUSPENDED':
        return <ClockCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const parseFeatures = (featuresString) => {
    try {
      return JSON.parse(featuresString || '[]');
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div className="subscription-loading">
        <Spin size="large" tip="Loading subscription details..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div className="subscription-details">
      <h2 className="section-title">My Subscription</h2>
      
      {subscriptions.length > 0 ? (
        <div className="active-subscriptions">
          {subscriptions.map((subscription, index) => (
            <Card key={subscription.subscriptionId || index} className="current-subscription-card" style={{ marginBottom: 16 }}>
              <Row gutter={[24, 24]}>
                <Col xs={24} lg={16}>
                  <div className="subscription-info">
                    <div className="plan-header">
                      <CrownOutlined className="plan-icon" />
                      <div className="plan-details">
                        <h3 className="plan-name">{subscription.planName}</h3>
                        <p className="plan-description">{subscription.description}</p>
                      </div>
                    </div>

                    <div className="subscription-meta">
                      <Row gutter={[16, 16]}>
                        <Col xs={12} sm={8}>
                          <div className="meta-item">
                            <DollarOutlined className="meta-icon" />
                            <div className="meta-content">
                              <span className="meta-label">Price</span>
                              <span className="meta-value">
                                ₹{subscription.price} {subscription.currency}
                              </span>
                            </div>
                          </div>
                        </Col>
                        <Col xs={12} sm={8}>
                          <div className="meta-item">
                            <CalendarOutlined className="meta-icon" />
                            <div className="meta-content">
                              <span className="meta-label">Duration</span>
                              <span className="meta-value">{subscription.duration}</span>
                            </div>
                          </div>
                        </Col>
                        <Col xs={12} sm={8}>
                          <div className="meta-item">
                            <CheckCircleOutlined className="meta-icon" />
                            <div className="meta-content">
                              <span className="meta-label">Status</span>
                              <Tag 
                                color={getStatusColor(subscription.status)}
                                icon={getStatusIcon(subscription.status)}
                              >
                                {subscription.status}
                              </Tag>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div className="subscription-dates">
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <div className="date-item">
                            <span className="date-label">Start Date:</span>
                            <span className="date-value">{formatDate(subscription.startDate)}</span>
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div className="date-item">
                            <span className="date-label">End Date:</span>
                            <span className="date-value">{formatDate(subscription.endDate)}</span>
                          </div>
                        </Col>
                      </Row>
                    </div>

                  </div>
                </Col>
                
                <Col xs={24} lg={8}>
                  <div className="subscription-actions">
                    <Button type="primary" size="large" block>
                      Manage Subscription
                    </Button>
                    <Button size="large" block style={{ marginTop: 12 }}>
                      Download Invoice
                    </Button>
                    {subscription.autoRenew && (
                      <Button type="link" size="large" block>
                        Disable Auto-Renewal
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="no-subscription-card">
          <Empty
            description="No active subscription found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" size="large" href="/pricing">
              Browse Plans
            </Button>
          </Empty>
        </Card>
      )}

      {subscriptionHistory.length > 0 && (
        <div className="subscription-history">
          <h3 className="section-title">Subscription History</h3>
          <Timeline>
            {subscriptionHistory.map((item, index) => (
              <Timeline.Item
                key={index}
                color={getStatusColor(item.status)}
                dot={getStatusIcon(item.status)}
              >
                <Card size="small">
                  <div className="history-item">
                    <div className="history-header">
                      <span className="plan-name">{item.planName}</span>
                      <Tag color={getStatusColor(item.status)}>
                        {item.status}
                      </Tag>
                    </div>
                    <div className="history-dates">
                      <span>{formatDate(item.startDate)} - {formatDate(item.endDate)}</span>
                    </div>
                    {item.cancellationDate && (
                      <div className="cancellation-info">
                        <span>Cancelled on: {formatDate(item.cancellationDate)}</span>
                        {item.cancellationReason && (
                          <span>Reason: {item.cancellationReason}</span>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              </Timeline.Item>
            ))}
          </Timeline>
        </div>
      )}
    </div>
  );
};

export default SubscriptionDetails;
